import type { AxiosResponse } from 'axios'
import type { RowSelectionState, ColumnDef, CellContext, HeaderContext } from '@tanstack/vue-table'
import { UploadIcon } from 'lucide-vue-next'
import { Checkbox } from '~/components/ui/checkbox'
import { Button } from '~/components/ui/button'
import BarsArrowUp from '~/components/BarsArrowUp.vue'
import ShadDatePicker from '~/components/ShadDatePicker.vue'
import InfiniteScrollDropdown from '~/components/InfiniteScrollDropdown.vue'
import TextInput from '~/components/TextInput.vue'

export const createColumns = (
  isInEditMode: Ref<boolean>,
  newSubmission: Ref<Partial<NewSubmissionTemplateData>>,
  rowSelection: Ref<RowSelectionState>,
  assetNameQuery: Ref<string>,
  assetsPageNumber: Ref<number>,
  listAssetsPromise: Ref<() => Promise<AxiosResponse<PaginatedResponse<OptionItem>>>>,
  rawDataKeys: ComputedRef<string[]>,
  onViewDocuments: (documents: EsgMetricRawData['supporting_documents']) => void,
): ColumnDef<EsgSubmissionTemplateData>[] => [
  {
    accessorKey: 'activity_date',
    id: 'activity_date',
    minSize: 240,
    maxSize: 280,
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const sortIconColor = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500' : ''

      return h('div', { class: 'flex flex-col gap-2.5' }, [
        h('div', { class: 'flex justify-between items-center' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'activity date'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(isSorted === 'asc'),
          }, () => h('div', {
            style: isSorted === 'desc' ? 'transform: rotate(180deg)' : '',
          }, [
            h(BarsArrowUp, { customStyle: `size-5 ${sortIconColor}` }),
          ])),
        ]),
        isInEditMode.value && h('div', { class: 'pb-2' }, [
          h(ShadDatePicker, {
            'onUpdate:modelValue': value => newSubmission.value.activity_date = value,
          }),
        ]),
      ])
    },
    cell: ({ row, column }) => {
      const { $moment } = useNuxtApp()
      const formattedDate = $moment(row.getValue(column.id)).format('DD/MM/YYYY')
      return h('div', { class: 'flex items-center gap-4' }, [
        isInEditMode.value && h(Checkbox, {
          'onUpdate:checked': (value: boolean) => {
            const rowId = row.original.id
            const newSelection = { ...rowSelection.value }
            if (value) {
              newSelection[rowId] = true
            }
            else {
              const newSelection = Object.fromEntries(
                Object.entries(rowSelection.value).filter(([key]) => key !== rowId.toString()),
              )
              rowSelection.value = newSelection
            }
            row.toggleSelected(!!value)
          },
        }),
        h('span', { class: 'text-md text-foreground truncate' }, formattedDate),
      ])
    },
  },
  {
    accessorKey: 'asset.name',
    id: 'asset',
    minSize: 240,
    maxSize: 280,
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const assetSortIconColor = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500' : ''

      return h('div', { class: 'flex flex-col gap-2.5' }, [
        h('div', { class: 'flex justify-between items-center' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'asset'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(isSorted === 'asc'),
          }, () => h('div', {
            style: isSorted === 'desc' ? 'transform: rotate(180deg)' : '',
          }, [
            h(BarsArrowUp, { customStyle: `size-5 ${assetSortIconColor}` }),
          ])),
        ]),
        isInEditMode.value && h('div', { class: 'pb-2' }, [
          h(InfiniteScrollDropdown, {
            'modelValue': assetNameQuery.value,
            'onUpdate:modelValue': value => assetNameQuery.value = value as string,
            'onUpdate:selected': (value) => {
              newSubmission.value.asset = value as OptionItem
            },
            'pageNumber': assetsPageNumber.value,
            'onUpdate:pageNumber': value => assetsPageNumber.value = value as number,
            'uniqueKey': 'user-assignment',
            'placeholder': 'Select asset',
            'fetchSuggestionsPromise': listAssetsPromise.value,
            'onFetchNextPage': () => assetsPageNumber.value++,
          }),
        ]),
      ])
    },
  },
  ...rawDataKeys.value.map(key => ({
    id: `raw_data.${key}`,
    minSize: 240,
    maxSize: 280,
    accessorFn: (row: EsgSubmissionTemplateData) => row.raw_data[key] || '-',
    header: (context: HeaderContext<EsgSubmissionTemplateData, unknown>) => {
      const { $moment } = useNuxtApp()
      const columnKey = context.column.id
      const rows = context.table.getRowModel().rows
      const firstValue = rows.length ? rows[0].getValue(columnKey) as string | number : ''
      const isDateValue = firstValue && typeof firstValue === 'string' && $moment(firstValue, $moment.ISO_8601, true).isValid()

      return h('div', { class: 'flex flex-col gap-2.5' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, key.replace(/_/g, ' ')),
        isInEditMode.value && h('div', { class: 'pt-2' }, [
          isDateValue
            ? h(ShadDatePicker, {
              'onUpdate:modelValue': value => newSubmission.value.raw_data![key] = value !== undefined ? value : '',
            })
            : h(TextInput, {
              onInput: value => newSubmission.value.raw_data![key] = value !== undefined ? value : '',
              placeholder: 'Enter value',
            }),
        ]),
      ])
    },
    cell: (context: CellContext<EsgSubmissionTemplateData, unknown>) => {
      const { $moment } = useNuxtApp()
      const value = context.getValue() as string | number
      const isDateValue = value && typeof value === 'string' && $moment(value, $moment.ISO_8601, true).isValid()
      const formattedValue = isDateValue ? $moment(value).format('DD/MM/YYYY') : value

      return h('span', { class: 'text-sm text-theme-text-black' }, formattedValue)
    },
  })),
  {
    id: 'supporting_documents',
    maxSize: 140,
    header: () => {
      return h('div', { class: 'flex flex-col gap-2.5' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold text-center' }, 'Attachments'),
        isInEditMode.value && h('div', { class: 'flex items-center justify-center' }, [
          h(Button, {
            class: 'text-theme-text-black text-sm',
            variant: 'link',
          }, () => [
            h(UploadIcon),
          ]),
        ]),
      ])
    },
    accessorKey: 'supporting_documents',
    cell: ({ row }) => {
      const documents = row.original.supporting_documents
      const count = documents.length

      if (count === 0) {
        return h('span', { class: 'text-sm text-gray-400 text-center px-3 block' }, '0')
      }

      return h(Button, {
        class: 'text-theme-text-black text-center text-sm !py-0 w-full h-auto',
        variant: 'link',
        onClick: () => onViewDocuments?.(documents),
      }, () => `${count}`)
    },
  },
]
